import { CSSProperties, useState, type JSX } from 'react'

import Select, {
  CSSObjectWithLabel,
  GroupBase,
  OnChangeValue,
  SelectComponentsConfig,
  SingleValueProps,
  StylesConfig,
  Theme,
} from 'react-select'

import styles from './dropdown.module.scss'

export type DropDownProps<T> = {
  onChange: (value: ItemType<T>) => Promise<void> | void
  options: ItemType<T>[]
  initialValue: ItemType<T> | null
  id: string
  width?: number | string
  height?: number | string
  error?: boolean
  additionalStyles?: CSSProperties
  background?: string
  placeHolder?: string
  onMenuOpen?: () => void
  disabled?: boolean
  padding?: number
  marginTop?: number
  border?: string
  selectedColor?: string
  hoverColor?: string
  focusColor?: string
  singleValue?: CSSProperties
  customPlaceholder?: CSSProperties
  menuStyles?: CSSProperties
  optionStyle?: CSSProperties
  classNamePrefix?: string
  menuPlacement?: 'bottom' | 'top' | 'auto'
  borderRadius?: number | string
  components?: Partial<SelectComponentsConfig<ItemType<T>, false, GroupBase<ItemType<T>>>> | undefined
  ariaLabelledBy?: string
  ariaLabel?: string
  // overflowScrolling?: string;
  isSearchable?: boolean
}

export type ItemType<T> = {
  value: T
  label: string
}

export default function DropDown<T>({
  id,
  error,
  background = '#FCF2FF',
  selectedColor = '#FCF2FF',
  hoverColor = '#FCF2FF',
  placeHolder = 'Please select...',
  width = '100%',
  onChange,
  onMenuOpen,
  disabled = false,
  options,
  height = 40,
  additionalStyles,
  initialValue,
  padding = 10,
  marginTop = 10,
  border = '1px solid #2011501A',
  singleValue,
  customPlaceholder,
  // overflowScrolling = 'touch',
  focusColor,
  menuStyles,
  optionStyle,
  classNamePrefix,
  menuPlacement = 'auto',
  borderRadius = 10,
  components,
  ariaLabel,
  ariaLabelledBy,
  isSearchable = false,
}: DropDownProps<T>): JSX.Element {
  const [value, setValue] = useState<ItemType<T> | null>(initialValue)

  function handleChange(selectedOption: OnChangeValue<ItemType<T>, false>): void {
    if (selectedOption) {
      setValue(selectedOption)
      onChange(selectedOption)
    }
  }

  const customStyles: StylesConfig<ItemType<T>, false> = {
    control: (style, state) => {
      let bgColor = background
      if (state.isFocused && focusColor) {
        bgColor = focusColor
      }
      return {
        ...style,
        boxShadow: 'none',
        width,
        background: error ? '#ffd4e0 !important;' : `${bgColor} 0% 0% no-repeat padding-box`,
        border: error ? '3px solid #ff2b64 !important;' : border,
        borderRadius,
        height,
        cursor: state.isDisabled ? 'not-allowed' : 'auto',
        minHeight: height,
        '&:hover': {
          background: error ? '#ffd4e0 !important;' : `${focusColor || background} 0% 0% no-repeat padding-box`,
        },
        ...additionalStyles,
      } as CSSObjectWithLabel
    },
    container: (style, state) => {
      return {
        ...style,
        width,
        background: `${background} 0% 0% no-repeat padding-box`,
        cursor: state.isDisabled ? 'not-allowed' : 'auto',
        height,
        // overflowScrolling: overflowScrolling,
        ...additionalStyles,
      } as CSSObjectWithLabel
    },
    valueContainer: (style, state) => {
      return {
        ...style,
        width: '75%',
        height,
        overflow: 'visible',
        cursor: state.isDisabled ? 'not-allowed' : 'auto',
      } as CSSObjectWithLabel
    },
    placeholder: (style) => {
      return {
        ...style,
        top: customPlaceholder?.top,
        position: customPlaceholder?.position,
        fontSize: customPlaceholder?.fontSize,
      } as CSSObjectWithLabel
    },
    menu: (style) => {
      return {
        ...style,
        width,
        background: '#FFFFFF 0% 0% no-repeat padding-box',
        boxShadow: '0px 6px 12px #15181C1A',
        borderRadius,
        marginTop,
        zIndex: 200,
        overflow: 'auto',
        padding,
        // overflowScrolling: overflowScrolling,
        ...additionalStyles,
        ...menuStyles,
      } as CSSObjectWithLabel
    },
    option: (style, state) => {
      let bgColor = 'transparent'
      if (state.isSelected) {
        bgColor = selectedColor
      } else if (state.isFocused) {
        bgColor = hoverColor
      }
      return {
        ...style,
        borderRadius: 6,
        marginTop,
        padding,
        fontWeight: 'normal',
        letterSpacing: 0,
        color: '#201150',
        backgroundColor: bgColor,
        ...optionStyle,
      } as CSSObjectWithLabel
    },
    // TODO: find a library more Typescript ready
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    singleValue: (
      style,
      state: SingleValueProps<ItemType<T>> & {
        isSelected?: boolean
        isFocused?: boolean
      },
    ) => {
      let bgColor = 'transparent'
      if (state.isSelected || state.isFocused) {
        bgColor = background
      }
      return {
        ...style,
        backgroundColor: bgColor,
        color: '#201150',
        padding: singleValue?.padding,
        lineHeight: singleValue?.lineHeight,
        height: singleValue?.height,
        fontSize: singleValue?.fontSize,
      }
    },
    dropdownIndicator: (style) => {
      return {
        ...style,
        paddingTop: padding - 2,
        paddingBottom: padding - 2,
        paddingLeft: padding,
        paddingRight: padding,
      } as CSSObjectWithLabel
    },
  }

  return (
    <div className={styles['dropdown-container']}>
      <Select
        aria-labelledby={ariaLabelledBy}
        aria-label={ariaLabel}
        components={components}
        classNamePrefix={classNamePrefix}
        isDisabled={disabled}
        isSearchable={isSearchable}
        onMenuOpen={onMenuOpen}
        menuPlacement={menuPlacement}
        instanceId={id}
        value={value}
        onChange={handleChange}
        options={options}
        styles={customStyles}
        placeholder={placeHolder}
        theme={(theme: Theme): Theme => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#FCF2FF',
            primary75: '#FCF2FF',
            primary50: '#FCF2FF',
            primary25: '#FCF2FF',
          },
        })}
      />
    </div>
  )
}
